<template>
  <div :id="id" class="chapter-section panel">
    <p class="panel-heading">{{ $t(`chapter6.title`) }}</p>

    <fieldset class="box">
      <legend>{{ $t('chapter6.q0.title') }}</legend>
      <b-radio
        :required="true"
        id="c6q0-1"
        :native-value="false"
        v-model="study.datenspeicherung.enthaeltNichtPseudonymisierteDaten"
        @change="onEnthaeltNichtPseudonymisierteDatenChange"
      >
        {{ $t('chapter6.q0.enthaeltNichtPseudonymisierteDatenFalse') }}
      </b-radio>
      <b-radio
        :required="true"
        id="c6q0-2"
        :native-value="true"
        v-model="study.datenspeicherung.enthaeltNichtPseudonymisierteDaten"
        @change="save"
      >
        {{ $t('chapter6.q0.enthaeltNichtPseudonymisierteDatenTrue') }}
      </b-radio>

      <InfoToggleNotification
        v-if="study.datenspeicherung.enthaeltNichtPseudonymisierteDaten"
        :toggle="false"
      >
        {{ $t('chapter6.q0.info') }}
      </InfoToggleNotification>

      <b-field
        v-if="showIdentifizierendeDaten"
        :label="$t('chapter6.q0.title2')"
        :type="
          store.getFieldError('datenspeicherung', 'verwendeteIdentifizierendeDaten')
            ? 'is-danger'
            : ''
        "
        :message="store.getFieldError('datenspeicherung', 'verwendeteIdentifizierendeDaten')"
      >
        <b-input
          :required="true"
          v-model="study.datenspeicherung.verwendeteIdentifizierendeDaten"
          @blur="save"
        />
      </b-field>
    </fieldset>

    <fieldset class="box">
      <b-field
        :label="$t('chapter6.q1.title')"
        :type="store.getFieldError('datenspeicherung', 'pseudonymisierung') ? 'is-danger' : ''"
        :message="store.getFieldError('datenspeicherung', 'pseudonymisierung')"
      >
        <div v-for="option in pseudonymisierungOptions" :key="option">
          <b-checkbox
            :required="true"
            :native-value="option"
            v-model="study.datenspeicherung.pseudonymisierung"
            @change="onPseudonymisierungChange"
          >
            {{ $t(`chapter6.q1.${option}`) }}
          </b-checkbox>
          <fieldset
            v-if="
              study.datenspeicherung.pseudonymisierung.includes('partner') && option === 'partner'
            "
            class="box"
          >
            <b-field :label="$t(`chapter6.q1a.title`)">
              <div v-for="option in pseudonymisierungPartnerOptions" :key="option">
                <b-checkbox
                  :native-value="option"
                  v-model="study.datenspeicherung.pseudonymisierungPartner"
                  @change="save"
                >
                  {{ $t(`chapter6.q1a.${option}`) }}
                </b-checkbox>
              </div>
            </b-field>
          </fieldset>
          <label
            v-if="
              study.datenspeicherung.pseudonymisierung.includes('sonstige') && option === 'sonstige'
            "
          >
            <b-input
              :placeholder="$t(`chapter6.q1.sonstigePlaceholder`)"
              v-model="study.datenspeicherung.pseudonymisierungSonstige"
              @blur="save"
            />
          </label>
        </div>
      </b-field>
    </fieldset>

    <fieldset class="box">
      <legend>{{ $t('chapter6.q2.title') }}</legend>
      <b-field
        :label="$t('chapter6.q2.text')"
        :type="store.getFieldError('datenspeicherung', 'speicherFormat') ? 'is-danger' : ''"
        :message="store.getFieldError('datenspeicherung', 'speicherFormat')"
      >
        <div v-for="option in speicherFormatOptions" :key="option">
          <b-checkbox
            :required="true"
            :native-value="option"
            v-model="study.datenspeicherung.speicherFormat"
            @change="onChangeSpeicherFormat"
          >
            {{ $t(`chapter6.q2.${option}`) }}
          </b-checkbox>
          <b-field
            v-if="option === 'digital' && study.datenspeicherung.speicherFormat.includes('digital')"
          >
            <div style="margin-left: 1.5rem">
              <b-checkbox
                v-model="study.datenspeicherung.speicherFormatDigitalMitPasswort"
                :native-value="true"
                @change="save"
              >
                {{ $t(`chapter6.q2a.text`) }}
              </b-checkbox>
            </div>
          </b-field>
        </div>
      </b-field>
    </fieldset>

    <fieldset class="box">
      <b-field
        :label="$t('chapter6.q3.title')"
        :type="
          store.getFieldError('datenspeicherung', 'speicherungWaehrendErhebung') ? 'is-danger' : ''
        "
        :message="store.getFieldError('datenspeicherung', 'speicherungWaehrendErhebung')"
      >
        <div v-for="option in speicherungErhebungOptions" :key="option">
          <b-checkbox
            :required="true"
            :native-value="option"
            v-model="study.datenspeicherung.speicherungWaehrendErhebung"
            @change="onSpeicherungWaehrendErhebungChange"
          >
            {{ $t(`chapter6.q3.${option}`) }}
          </b-checkbox>
          <b-field
            :label="$t('chapter6.q3.sonstigeText')"
            v-if="
              study.datenspeicherung.speicherungWaehrendErhebung.includes('sonstige') &&
              option === 'sonstige'
            "
          >
            <b-input
              v-model="study.datenspeicherung.speicherungWaehrendErhebungSonstige"
              @blur="save"
            />
          </b-field>
        </div>
      </b-field>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStudyStore } from '@/stores/studyStore'
import InfoToggleNotification from '@/components/InfoText.vue'
import { BCheckbox, BField, BInput, BRadio, BSwitch } from 'buefy'

defineProps<{ id: string; modelValue?: object }>()

const store = useStudyStore()
const study = store.study
const title = 'Datenspeicherung'

const pseudonymisierungOptions = ['diz', 'ibdf', 'partner', 'sonstige']
const pseudonymisierungPartnerOptions = ['partner', 'umfGu']
const speicherFormatOptions = ['papier', 'digital', 'liegtNichtVor']
const speicherungErhebungOptions = ['digital', 'klinet', 'ecrf', 'analog', 'sonstige']

const save = () => {
  store.updateChapter('datenspeicherung', study.datenspeicherung)
}

const showIdentifizierendeDaten = computed(
  () => study.datenspeicherung.enthaeltNichtPseudonymisierteDaten,
)

function onEnthaeltNichtPseudonymisierteDatenChange() {
  if (!study.datenspeicherung.enthaeltNichtPseudonymisierteDaten) {
    study.datenspeicherung.verwendeteIdentifizierendeDaten = undefined
  }
  save()
}

function onPseudonymisierungChange() {
  if (!(study.datenspeicherung.pseudonymisierung || []).includes('partner')) {
    study.datenspeicherung.pseudonymisierungPartner = []
  }
  if (!(study.datenspeicherung.pseudonymisierung || []).includes('sonstige')) {
    study.datenspeicherung.pseudonymisierungSonstige = undefined
  }
  save()
}

function onSpeicherungWaehrendErhebungChange() {
  if (!(study.datenspeicherung.speicherungWaehrendErhebung || []).includes('sonstige')) {
    study.datenspeicherung.speicherungWaehrendErhebungSonstige = undefined
  }
  save()
}

function onChangeSpeicherFormat() {
  if (!(study.datenspeicherung.speicherFormat || []).includes('digital')) {
    study.datenspeicherung.speicherFormatDigitalMitPasswort = false
  }
  save()
}
</script>

<style scoped></style>
